// Screen 8 - connections & vault (slice-2-onboarding.md §11; extends mockup S3). Whole
// page is owner/admin only (§11.2 - resolved as full-page denial, not a rows-visible/
// buttons-hidden read-only view; flagged there for design-reviewer confirmation).
import { useEffect, useState } from "react";
import { isErr } from "@nightcrew/shared";
import type { Connection } from "@nightcrew/shared";
import { DeniedCard } from "../components/DeniedCard.js";
import { listConnections, revokeConnection, restoreConnection } from "../api/connections.js";
import { isOwnerOrAdmin, useSession } from "../state/session.js";

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | { readonly kind: "empty" }
  | { readonly kind: "ready"; readonly connections: readonly Connection[] };

export const Connections = (): React.JSX.Element => {
  const { state: session } = useSession();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [confirmingRevoke, setConfirmingRevoke] = useState<Connection | null>(null);

  const canManage = isOwnerOrAdmin(session);

  useEffect(() => {
    if (!canManage) return;
    const cancelled = { current: false };
    void (async () => {
      const result = await listConnections();
      if (cancelled.current) return;
      if (isErr(result)) {
        setState({ kind: "error" });
        return;
      }
      setState(
        result.value.connections.length === 0
          ? { kind: "empty" }
          : { kind: "ready", connections: result.value.connections },
      );
    })();
    return () => {
      cancelled.current = true;
    };
  }, [canManage]);

  if (!canManage) {
    return <DeniedCard message="Ask an owner or admin, or ask them to make you an admin." />;
  }

  if (state.kind === "loading") {
    return (
      <div className="stack gap12">
        <div className="card skeleton-card" />
        <div className="card skeleton-card" />
      </div>
    );
  }

  if (state.kind === "error") {
    return (
      <div className="card state-card">
        <p>We could not load your connections. Try again.</p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => {
            setState({ kind: "loading" });
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (state.kind === "empty") {
    // m5: was a bare <p>, no page frame or heading - now the same page frame as the
    // populated/error states (a page heading always renders) plus a designed empty-state
    // well (brand.css's own .well surface, brand.css:113) rather than plain floating text.
    return (
      <div>
        <h1 className="h3">Connections &amp; vault</h1>
        <div className="well pad mt16 center">
          <p className="muted">No connections yet. They are added as you connect an app.</p>
        </div>
      </div>
    );
  }

  const handleRevoke = async (connection: Connection): Promise<void> => {
    const result = await revokeConnection(connection.id);
    setConfirmingRevoke(null);
    if (isErr(result)) return;
    setState((prev) =>
      prev.kind === "ready"
        ? {
            kind: "ready",
            connections: prev.connections.map((c) => (c.id === result.value.id ? result.value : c)),
          }
        : prev,
    );
  };

  const handleRestore = async (connection: Connection): Promise<void> => {
    const result = await restoreConnection(connection.id);
    if (isErr(result)) return;
    setState((prev) =>
      prev.kind === "ready"
        ? {
            kind: "ready",
            connections: prev.connections.map((c) => (c.id === result.value.id ? result.value : c)),
          }
        : prev,
    );
  };

  return (
    <div>
      <h1 className="h3">Connections &amp; vault</h1>
      <div className="card mt16" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "var(--space-11) var(--space-15)" }}>
          {state.connections.map((connection) => (
            <div className="conn-row" key={connection.id}>
              <span className="logo" aria-hidden="true">
                {connection.platform.slice(0, 2).toUpperCase()}
              </span>
              <div className="grow">
                <b style={{ fontSize: 13.5 }}>{connection.platform}</b>
                <div className="muted" style={{ fontSize: 12 }}>
                  {connection.mode === "federated" ? "Federated" : "Scoped token"}
                </div>
              </div>
              <span
                className={`tag ${
                  connection.status === "active"
                    ? "good"
                    : connection.status === "needs_action"
                      ? "risk"
                      : "warn"
                }`}
              >
                {connection.status === "active"
                  ? "Active"
                  : connection.status === "needs_action"
                    ? "Needs action"
                    : "Revoked"}
              </span>
              {connection.status === "active" ? (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    setConfirmingRevoke(connection);
                  }}
                >
                  Revoke {connection.platform}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void handleRestore(connection);
                  }}
                >
                  Reconnect {connection.platform}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      <div className="well pad mt16">
        <b style={{ fontSize: 12.5 }}>
          Secrets live in the vault, leased for seconds, never in a prompt or a log.
        </b>{" "}
        Once you reconnect, the crew catches up on anything it missed and clears the banner.
      </div>

      {confirmingRevoke !== null ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Revoke ${confirmingRevoke.platform} access`}
          className="card pad mt16"
        >
          <p>Revoke {confirmingRevoke.platform} access? Work will pause until you reconnect.</p>
          <div className="row gap8 mt12">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                void handleRevoke(confirmingRevoke);
              }}
            >
              Confirm
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => {
                setConfirmingRevoke(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
